import { apiFetchJson } from '../http/apiFetch';
import type { AuditLog, AuditLogFilters, PagedAuditLogs } from '../../models/auditLog';

function asString(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) {
    return fallback;
  }
  return String(value);
}

function asOptionalString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const str = String(value).trim();
  return str === '' ? null : str;
}

function asNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function normalizeAuditLog(row: unknown): AuditLog | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const a = row as Record<string, unknown>;
  const id = asString(a.id ?? a.Id).trim();
  if (!id) {
    return null;
  }
  return {
    id,
    actorId: asOptionalString(a.actorId ?? a.ActorId),
    actorEmail: asString(a.actorEmail ?? a.ActorEmail).trim(),
    actionType: asString(a.actionType ?? a.ActionType).trim(),
    targetType: asOptionalString(a.targetType ?? a.TargetType),
    targetId: asOptionalString(a.targetId ?? a.TargetId),
    targetName: asOptionalString(a.targetName ?? a.TargetName),
    metadataJson: asOptionalString(a.metadataJson ?? a.MetadataJson),
    createdAt: asString(a.createdAt ?? a.CreatedAt),
  };
}

function normalizePagedAuditLogs(result: unknown): PagedAuditLogs {
  if (!result || typeof result !== 'object') {
    return { items: [], totalCount: 0, page: 1, pageSize: 0 };
  }
  const r = result as Record<string, unknown>;
  const rawItems = r.items ?? r.Items;
  const items = Array.isArray(rawItems)
    ? rawItems.map(normalizeAuditLog).filter((a): a is AuditLog => Boolean(a))
    : [];
  return {
    items,
    totalCount: asNumber(r.totalCount ?? r.TotalCount),
    page: asNumber(r.page ?? r.Page),
    pageSize: asNumber(r.pageSize ?? r.PageSize),
  };
}

export async function fetchAuditLogs(
  getAccessToken: () => string | undefined,
  filters: AuditLogFilters,
  page: number,
  pageSize: number
): Promise<PagedAuditLogs> {
  const params = new URLSearchParams();
  if (filters.actionType?.trim()) {
    params.set('actionType', filters.actionType.trim());
  }
  if (filters.companyId?.trim()) {
    params.set('companyId', filters.companyId.trim());
  }
  if (filters.fromDate?.trim()) {
    params.set('fromDate', filters.fromDate.trim());
  }
  if (filters.toDate?.trim()) {
    params.set('toDate', filters.toDate.trim());
  }
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));

  const result = await apiFetchJson<unknown>(`/api/admin/audit-logs?${params.toString()}`, {
    method: 'GET',
    getAccessToken: async () => getAccessToken(),
  });

  return normalizePagedAuditLogs(result);
}
